// docs/78 BW5: pings on the World chart. Right-click (Shift for "danger") drops a ring on the chart
// that every player in the World sees for a few seconds, in the pinger's colour with their name:
// the `world_ping` event table (`band.rs`), tenths of a chart cell, kinds as the board's pings.
import { useEffect, useState } from 'react';
import { tables, type EventContext } from '../../../bindings';
import type { Identity } from 'spacetimedb';
import { getConn, identityHex } from '../../../core/net/connection';
import { rpc } from '../../../core/net/reducers';
import { playSfx } from '../../../core/audio';
import { deviceNow } from '../../../core/time';
import { PING_MS, PING_SOUND, mayPing } from '../../../core/pings';

export interface WorldPing { id: number; owner: string; x: number; y: number; kind: number; at: number }

/** How long a ping stays on the chart (ms): the board's too (docs/83). */
export const OW_PING_MS = PING_MS;

interface PingRow { worldId: bigint; owner: Identity; x: number; y: number; kind: number }
let seq = 1;

/** Pings `x, y` (chart cells) for everyone in the World; false while my last ping is still fresh. */
export function pingChart(worldId: bigint, x: number, y: number, kind: number): boolean {
  if (!mayPing()) return false;
  void rpc.pingWorld(worldId, Math.round(x * 10), Math.round(y * 10), kind);
  return true;
}

/** The World's live pings, subscribed while the chart is up. */
export function useWorldPings(worldId: bigint | undefined): WorldPing[] {
  const [pings, setPings] = useState<WorldPing[]>([]);
  useEffect(() => {
    const c = getConn();
    if (!c || worldId === undefined) return;
    const sub = c.subscriptionBuilder()
      .onError((_ctx, err?: Error) => console.error('[ping] subscription error', err))
      .subscribe([tables.worldPing.where((r) => r.worldId.eq(worldId))]);
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const onRow = (_ctx: EventContext, row: PingRow) => {
      if (row.worldId !== worldId) return;
      const p: WorldPing = { id: seq++, owner: identityHex(row.owner), x: row.x / 10, y: row.y / 10, kind: row.kind, at: deviceNow() };
      setPings((xs) => [...xs.filter((q) => q.at > p.at - OW_PING_MS), p]);
      playSfx(PING_SOUND[row.kind] ?? 'ping.look');
      const t = setTimeout(() => { timers.delete(t); setPings((xs) => xs.filter((q) => q.id !== p.id)); }, OW_PING_MS);
      timers.add(t);
    };
    c.db.worldPing.onInsert(onRow);
    return () => {
      c.db.worldPing.removeOnInsert(onRow);
      sub.unsubscribe();
      for (const t of timers) clearTimeout(t);
    };
  }, [worldId]);
  return pings;
}
